"use client";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/radix/tooltip";
import { SkeletonLoader } from "@/components/ui/skeleton";
import { approvalsInfo } from "@/info/approvals";
import { banksInfo } from "@/info/banks";
import { documentsInfo } from "@/info/documents";
import { SellerUnitsAndProjectsOne } from "@/types/sup";
import { toSentenceCase } from "@/utils/string/to-sentence-case";
import { Info } from "lucide-react";
import { StringList } from "../../string-list";
import { WaterSource } from "../seller-unit/water-source";

interface ProjectData {
	propertyType?: string;
	specifications?: string;
	area?: string;
	roads?: string[];
	reraId?: string;
}
interface ProjectDetailsProps {
	supOne: SellerUnitsAndProjectsOne;
	waterSourceString?: string;
	ageString?: string;
	isProjectPage?: boolean;
	isLoading?: boolean;
	projectData?: ProjectData;
}

export function ProjectDetails({
	supOne,
	waterSourceString,
	ageString,
	isProjectPage,
	isLoading = false,
	projectData,
}: ProjectDetailsProps) {
	// Check if property is a plot
	const isPlotProperty = supOne.propertyTypes?.some(type => type.toLowerCase() === "plot");

	// Project-specific details
	const projectRows = [
		{
			label: "Approvals",
			value: (
				<StringList
					className="text-right"
					data={supOne.approvals}
					info={approvalsInfo.bengaluru}
					fallback="Not specified"
				/>
			),
		},
		{
			label: "Bank Approvals",
			value: (
				<StringList className="text-right" data={supOne.bankApprovals} info={banksInfo} fallback="Not specified" />
			),
		},
		{
			label: "Documents",
			value: (
				<StringList className="text-right" data={supOne.documents} info={documentsInfo} fallback="Not specified" />
			),
		},
		// Lifts - hidden for plot properties
		...(!isPlotProperty ? [{ label: "Lifts", value: supOne.amenities?.includes("lift") ? "Yes" : "No" }] : []),
		...(!isPlotProperty
			? [
					{
						label: "Possession status",
						value: isProjectPage
							? [...new Set(supOne.possessionStatuses ?? [])].map(p => toSentenceCase(p)).join(", ") + ` (${ageString})`
							: "",
					},
				]
			: []),
		{ label: "Swimming Pool", value: supOne.amenities?.includes("swimmingPool") ? "Yes" : "No" },
		{ label: "Total Basements", value: supOne.totalBasements?.toString() || "" },
		{ label: "Total Blocks", value: supOne.totalBlocks?.toString() || "" },
		{ label: "Total Floors in the Building", value: supOne.totalFloors || "" },
		{ label: "Total Units", value: supOne.totalUnits?.toString() || "" },
		{
			label: "Water Source",
			value:
				waterSourceString || (supOne.waterSource ? <WaterSource waterSource={supOne.waterSource} fallback="" /> : ""),
		},
		{
			label: "Property Type",
			value: projectData?.propertyType || "",
		},
		{
			label: "Specifications",
			value: projectData?.specifications || "",
		},
		{
			label: "Area",
			value: projectData?.area ? projectData.area + " Acres" : "",
		},
		{
			label: "Roads",
			value: projectData?.roads?.join(", ") || "",
		},
		{
			label: "RERA ID",
			value: projectData?.reraId || "",
		},
	].filter(row => row.value);

	// Sort detail rows alphabetically by label
	const sortedProjectRows = [...projectRows].sort((a, b) => a.label.localeCompare(b.label));

	interface DetailRow {
		label: string;
		value: string | React.ReactNode;
		info?: string;
		available?: boolean;
	}

	const renderDetailRow = (row: DetailRow, index: number) => (
		<div key={index} className="flex justify-between items-center py-1.5 px-2 border-b border-gray-200">
			<span className="text-sm font-medium text-gray-600">{row.label}</span>
			<div className="flex items-center gap-1">
				{row.available || !isLoading ? (
					<span className="text-sm font-semibold text-primary-700">{row.value}</span>
				) : (
					<SkeletonLoader height="16px" width="30%" />
				)}
				{row.info && (
					<Tooltip>
						<TooltipTrigger asChild>
							<Info className="w-3.5 h-3.5 text-primary-700 cursor-help" />
						</TooltipTrigger>
						<TooltipContent>{row.info}</TooltipContent>
					</Tooltip>
				)}
			</div>
		</div>
	);

	return (
		<div className="bg-white p-2 mt-2">
			<div className="space-y-1">{sortedProjectRows.map((row, index) => renderDetailRow(row, index))}</div>
		</div>
	);
}
